<template>
  <canvas v-if="isPlaying" ref="canvasRef" class="confetti-burst" aria-hidden="true" />
</template>
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

interface Props {
  colors?: string[];
  particleCount?: number;
  // how long the burst plays before it's torn down
  durationMs?: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  color: string;
  round: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  colors: () => ['#0b5cff', '#4f8cff', '#86efac', '#fdba74', '#c4b5fd', '#fbbf24'],
  particleCount: 140,
  durationMs: 3200,
});

const emit = defineEmits<{ done: [] }>();

const FRAME_MS = 1000 / 60;

/* ---------------------------------- STATE --------------------------------- */
const canvasRef = ref<HTMLCanvasElement | null>(null);
const isPlaying = ref(true);
let frameId: number | null = null;

/* --------------------------------- METHODS -------------------------------- */
function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function createParticles(width: number, height: number): Particle[] {
  // two cannons from the lower corners, aimed up and inwards
  return Array.from({ length: props.particleCount }, (_, i) => {
    const fromLeft = i % 2 === 0;
    const angle = (fromLeft ? -60 : -120) + (Math.random() - 0.5) * 40;
    const speed = 9 + Math.random() * 9;
    const radians = (angle * Math.PI) / 180;
    return {
      x: fromLeft ? width * 0.1 : width * 0.9,
      y: height * 0.75,
      vx: Math.cos(radians) * speed,
      vy: Math.sin(radians) * speed,
      size: 5 + Math.random() * 5,
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.3,
      color: props.colors[i % props.colors.length],
      round: Math.random() < 0.3,
    };
  });
}

function stop() {
  if (frameId !== null) cancelAnimationFrame(frameId);
  frameId = null;
  isPlaying.value = false;
  emit('done');
}

function play() {
  const canvas = canvasRef.value;
  const ctx = canvas?.getContext('2d');
  if (!canvas || !ctx) return stop();

  const dpr = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);

  const particles = createParticles(width, height);
  const startedAt = performance.now();
  let lastFrameAt = startedAt;

  const tick = (now: number) => {
    const elapsed = now - startedAt;
    if (elapsed >= props.durationMs) return stop();

    // the physics are tuned per 60fps frame, scale them by the real frame time so the burst
    // plays at the same speed on high refresh rate screens. capped so a stalled tab doesn't jump
    const frames = Math.min((now - lastFrameAt) / FRAME_MS, 3);
    lastFrameAt = now;
    const drag = Math.pow(0.985, frames);

    // fade everything out over the last third of the run
    const fadeStart = props.durationMs * (2 / 3);
    ctx.globalAlpha = elapsed < fadeStart ? 1 : 1 - (elapsed - fadeStart) / (props.durationMs - fadeStart);
    ctx.clearRect(0, 0, width, height);

    for (const p of particles) {
      p.vx *= drag;
      p.vy = p.vy * drag + 0.35 * frames;
      p.x += p.vx * frames;
      p.y += p.vy * frames;
      p.rotation += p.spin * frames;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      }
      ctx.restore();
    }

    frameId = requestAnimationFrame(tick);
  };

  frameId = requestAnimationFrame(tick);
}

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(() => {
  if (prefersReducedMotion()) return stop();
  play();
});

onBeforeUnmount(() => {
  if (frameId !== null) cancelAnimationFrame(frameId);
});
</script>
<style scoped>
.confetti-burst {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 1080;
}
</style>
